/**
 * Logistics P&L — Vehicle Rates.
 * Pick a vendor → its vehicle → see the active contracted rate and set a new effective-dated
 * one (flat_monthly → monthly_rate, or per_day → daily_rate). One rate per dedicated vehicle.
 */
import React, { useEffect, useState } from 'react';
import { useVendorData } from '../../manpower-vendors/hooks/useVendorData';
import { ManpowerVehiclesApiService } from '../../../services/manpowerVehiclesApi';
import type { Vehicle } from '../../../services/manpowerVehiclesApi';
import { useVehicleRate, useSetRate } from '../hooks/useLogistics';

type Basis = 'flat_monthly' | 'per_day';
const todayIso = () => {
	const n = new Date();
	return new Date(n.getTime() - n.getTimezoneOffset() * 60_000).toISOString().slice(0, 10);
};
const money = (v?: string | number | null) => {
	const n = Number(v);
	return Number.isFinite(n) ? `₹${n.toLocaleString('en-IN')}` : '—';
};

export const VehicleRates: React.FC = () => {
	const { data: vendorsResp } = useVendorData();
	const vendors = vendorsResp?.data ?? [];
	const [vendorId, setVendorId] = useState<number | ''>('');
	const [vehicles, setVehicles] = useState<Vehicle[]>([]);
	const [vehicleId, setVehicleId] = useState<number | ''>('');

	const [basis, setBasis] = useState<Basis>('flat_monthly');
	const [amount, setAmount] = useState('');
	const [effectiveFrom, setEffectiveFrom] = useState(todayIso());
	const [toast, setToast] = useState<string | null>(null);

	const { data: current, isLoading: rateLoading } = useVehicleRate(
		vehicleId === '' ? undefined : vehicleId
	);
	const setRate = useSetRate();

	const showToast = (m: string) => {
		setToast(m);
		window.setTimeout(() => setToast(null), 3000);
	};

	// Load the chosen vendor's vehicles.
	useEffect(() => {
		if (vendorId === '') {
			setVehicles([]);
			setVehicleId('');
			return;
		}
		ManpowerVehiclesApiService.listVehicles(vendorId)
			.then(r => setVehicles(r.data ?? []))
			.catch(() => setVehicles([]));
	}, [vendorId]);

	// Seed the form from the current rate when it loads.
	useEffect(() => {
		if (!current) return;
		setBasis((current.billing_basis as Basis) ?? 'flat_monthly');
		setAmount(
			current.billing_basis === 'per_day'
				? current.daily_rate != null ? String(Number(current.daily_rate)) : ''
				: current.monthly_rate != null ? String(Number(current.monthly_rate)) : ''
		);
		setEffectiveFrom(todayIso());
	}, [current]);

	const amountNum = Number(amount);
	const canSubmit = vehicleId !== '' && amount !== '' && amountNum > 0 && /^\d{4}-\d{2}-\d{2}$/.test(effectiveFrom);

	const handleSave = async () => {
		try {
			await setRate.mutateAsync({
				vehicleId: vehicleId as number,
				body: {
					billing_basis: basis,
					...(basis === 'flat_monthly' ? { monthly_rate: amountNum } : { daily_rate: amountNum }),
					effective_from: effectiveFrom,
				},
			});
			showToast('Vehicle rate set');
		} catch (e) {
			showToast(e instanceof Error ? e.message : 'Failed to set rate');
		}
	};

	const inputCls =
		'rounded-md border border-gray-300 px-3 py-2 text-sm focus:border-green-500 focus:outline-none';

	return (
		<div className='min-h-screen bg-white p-6'>
			<div className='max-w-3xl mx-auto'>
				<div className='mb-6'>
					<h1 className='text-xl font-semibold text-gray-900'>Vehicle Rates</h1>
					<p className='text-sm text-gray-500'>
						Logistics P&amp;L — set a dedicated vehicle&apos;s contracted monthly/daily rate (effective-dated).
					</p>
				</div>

				{/* vendor + vehicle pickers */}
				<div className='mb-4 flex flex-wrap items-end gap-3'>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Vendor</label>
						<select value={vendorId} onChange={e => setVendorId(e.target.value === '' ? '' : Number(e.target.value))} className={`${inputCls} w-56`}>
							<option value=''>Select a vendor…</option>
							{vendors.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
						</select>
					</div>
					<div className='flex flex-col gap-1'>
						<label className='text-xs font-medium text-gray-700'>Vehicle</label>
						<select value={vehicleId} onChange={e => setVehicleId(e.target.value === '' ? '' : Number(e.target.value))} className={`${inputCls} w-56`} disabled={vendorId === ''}>
							<option value=''>{vendorId === '' ? 'Pick a vendor first' : 'Select a vehicle…'}</option>
							{vehicles.map(v => <option key={v.id} value={v.id}>{v.vehicle_number ?? `#${v.id}`}</option>)}
						</select>
					</div>
				</div>

				{vehicleId !== '' && (
					<div className='rounded-lg border border-gray-200 p-4'>
						{/* current rate */}
						<div className='mb-4 rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-700'>
							<div className='mb-1 font-medium text-gray-900'>Current rate (as of today)</div>
							{rateLoading ? 'Loading…' : current ? (
								<span>
									{current.billing_basis === 'per_day'
										? `Per day: ${money(current.daily_rate)}`
										: `Monthly: ${money(current.monthly_rate)}`}{' '}
									· basis {current.billing_basis} · from {current.effective_from}
								</span>
							) : <span className='text-gray-500'>No rate set for this vehicle.</span>}
						</div>

						<div className='text-xs font-semibold text-gray-900 mb-3'>Set new rate</div>
						<div className='flex flex-wrap items-end gap-3'>
							<div className='flex flex-col gap-1'>
								<label className='text-xs font-medium text-gray-700'>Billing basis</label>
								<select value={basis} onChange={e => setBasis(e.target.value as Basis)} className={inputCls}>
									<option value='flat_monthly'>flat_monthly</option>
									<option value='per_day'>per_day</option>
								</select>
							</div>
							<div className='flex flex-col gap-1'>
								<label className='text-xs font-medium text-gray-700'>
									{basis === 'per_day' ? 'Daily rate (₹)' : 'Monthly rate (₹)'} <span className='text-red-500'>*</span>
								</label>
								<input type='number' min={1} value={amount} onChange={e => setAmount(e.target.value)} placeholder='e.g. 45000' className={inputCls} />
							</div>
							<div className='flex flex-col gap-1'>
								<label className='text-xs font-medium text-gray-700'>Effective from <span className='text-red-500'>*</span></label>
								<input type='date' value={effectiveFrom} onChange={e => setEffectiveFrom(e.target.value)} className={inputCls} />
							</div>
							<button type='button' onClick={handleSave} disabled={!canSubmit || setRate.isPending} className='rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white hover:bg-green-700 disabled:opacity-50'>
								{setRate.isPending ? 'Saving…' : 'Save rate'}
							</button>
						</div>
					</div>
				)}
			</div>

			{toast && <div className='fixed bottom-6 right-6 z-50 rounded-md bg-gray-900 px-4 py-2 text-sm text-white shadow-lg'>{toast}</div>}
		</div>
	);
};

export default VehicleRates;
